import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronLeft, ChevronRight, Plus, X, Trash2, CalendarDays, Check,
  ClipboardList, CheckCircle2, RotateCcw,
} from 'lucide-react';
import GridMes, { TagDia } from '../../home/GridMes';
import {
  MESES, modalOverlay, modalCard, modalHeader, btnX, rotuloStyle, inputStyle, authHeaders,
} from '../../home/calendario-ui';
import { FONTE } from '../../home/ui';
import { CATEGORIAS_TAREFA, TIPO } from './agenda-tarefa-categorias';

// =====================================================================
// Calendário de Visitas do Operacional (DEM-00019, Fase 2)
// =====================================================================
// Plano de visitas dos Consultores de Operações. REUSA GridMes/TagDia da Home
// (peças visuais puras). O cumprimento é DERIVADO no backend — a tela só pinta:
// uma visita só fica "Realizada" quando as 3 avaliações da loja saíram na janela.
// Sem recorte por mercado: /lojas devolve as 13 lojas auditáveis (a trava Norte/Sul
// saiu na DEM-00034 — ficou só na Avaliação 360).

const COR = '#475569'; // identidade do módulo Operacional

// Cor da TAG por situação (texto + pastilha). Fora do grid, cor = situação.
const SITUACAO_VISUAL = {
  Realizado: { cor: '#047857', fundo: '#d1fae5', rotulo: 'Realizado' },
  'Em andamento': { cor: '#1d4ed8', fundo: '#dbeafe', rotulo: 'Em andamento' },
  Previsto: { cor: '#475569', fundo: '#f1f5f9', rotulo: 'Previsto' },
  Atrasado: { cor: '#b91c1c', fundo: '#fee2e2', rotulo: 'Atrasado' },
};
const ORDEM_SITUACAO = ['Atrasado', 'Em andamento', 'Previsto', 'Realizado'];

// Rótulo das 3 avaliações que compõem a visita (para o detalhe).
const AVALIACAO_LABEL = { a360: 'Avaliação 360', checklist: 'Auditoria Fin./Estoques', sa: 'Segurança do Alimento' };

const paraISO = (ano, mes, dia) => `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;

const formVazio = () => ({
  id: null, tipo: TIPO.VISITA, titulo: '', categoria: '',
  loja: '', dataInicio: '', dataFim: '', observacao: '',
});

function CalendarioOperacional() {
  const agora = new Date();
  const [ano, setAno] = useState(agora.getFullYear());
  const [mes, setMes] = useState(agora.getMonth() + 1); // 1..12
  const [itens, setItens] = useState([]);
  const [lojas, setLojas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const [modalAberto, setModalAberto] = useState(false);
  const [form, setForm] = useState(formVazio());
  const [salvando, setSalvando] = useState(false);
  const [confirmandoExcluir, setConfirmandoExcluir] = useState(false);

  // Lojas auditáveis para agendar (o backend devolve as 13; sem recorte por mercado).
  useEffect(() => {
    fetch('/api/operacional/agenda/lojas', { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => setLojas(d.lojas || []))
      .catch(() => { /* dropdown fica vazio; não trava a leitura */ });
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const de = paraISO(ano, mes, 1);
      const ate = paraISO(ano, mes, new Date(ano, mes, 0).getDate());
      const r = await fetch(`/api/operacional/agenda?de=${de}&ate=${ate}`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Não foi possível carregar o calendário.');
      const d = await r.json();
      setItens(d.itens || []);
    } catch (e) {
      setErro(e.message);
      setItens([]);
    } finally {
      setCarregando(false);
    }
  }, [ano, mes]);

  useEffect(() => { carregar(); }, [carregar]);

  // { diaDoMes: [item, …] } — cada item aparece em todos os dias que cobre.
  const porDia = useMemo(() => {
    const mapa = {};
    for (const it of itens) {
      for (const diaISO of it.dias || []) {
        const dia = Number(diaISO.slice(8, 10));
        (mapa[dia] = mapa[dia] || []).push(it);
      }
    }
    for (const dia of Object.keys(mapa)) {
      mapa[dia].sort((a, b) => ORDEM_SITUACAO.indexOf(a.situacao) - ORDEM_SITUACAO.indexOf(b.situacao));
    }
    return mapa;
  }, [itens]);

  // Placar do mês por situação (conta cada visita uma vez, não por dia).
  const placar = useMemo(() => {
    const p = { Atrasado: 0, 'Em andamento': 0, Previsto: 0, Realizado: 0 };
    for (const it of itens) if (p[it.situacao] != null) p[it.situacao] += 1;
    return p;
  }, [itens]);

  const irMes = (delta) => {
    let m = mes + delta;
    let a = ano;
    if (m < 1) { m = 12; a -= 1; }
    if (m > 12) { m = 1; a += 1; }
    setMes(m); setAno(a);
  };

  const abrirNovo = () => { setForm(formVazio()); setConfirmandoExcluir(false); setModalAberto(true); };
  const abrirItem = (it) => {
    setForm({
      id: it.ID,
      tipo: it.TIPO === TIPO.TAREFA ? TIPO.TAREFA : TIPO.VISITA,
      titulo: it.TITULO || '',
      categoria: it.CATEGORIA || '',
      loja: it.LOJA || '',
      dataInicio: it.DATA_INICIO,
      dataFim: it.DATA_FIM,
      observacao: it.OBSERVACAO || '',
    });
    setConfirmandoExcluir(false);
    setModalAberto(true);
  };

  const salvar = async () => {
    const ehTarefa = form.tipo === TIPO.TAREFA;
    if (!form.dataInicio || !form.dataFim) { setErro('Início e fim são obrigatórios.'); return; }
    if (form.dataFim < form.dataInicio) { setErro('A data de fim não pode ser anterior à de início.'); return; }
    if (ehTarefa) {
      if (!form.titulo.trim()) { setErro('O título da tarefa é obrigatório.'); return; }
      if (!form.categoria) { setErro('Escolha a categoria da tarefa.'); return; }
    } else if (!form.loja) { setErro('A loja é obrigatória na visita.'); return; }
    setSalvando(true);
    setErro('');
    try {
      const url = form.id ? `/api/operacional/agenda/${form.id}` : '/api/operacional/agenda';
      const body = ehTarefa
        ? {
          tipo: TIPO.TAREFA,
          titulo: form.titulo,
          categoria: form.categoria,
          loja: form.loja || null, // loja é opcional na tarefa
          dataInicio: form.dataInicio,
          dataFim: form.dataFim,
          observacao: form.observacao,
        }
        : {
          tipo: TIPO.VISITA,
          loja: form.loja,
          dataInicio: form.dataInicio,
          dataFim: form.dataFim,
          observacao: form.observacao,
        };
      const r = await fetch(url, {
        method: form.id ? 'PUT' : 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.message || 'Erro ao salvar.'); }
      setModalAberto(false);
      await carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  // Marca/retira a conclusão de uma TAREFA (a visita é derivada, não tem isto).
  const concluirTarefa = async (concluido) => {
    setSalvando(true);
    setErro('');
    try {
      const r = await fetch(`/api/operacional/agenda/${form.id}/conclusao`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ concluido }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.message || 'Erro ao atualizar a conclusão.'); }
      setModalAberto(false);
      await carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async () => {
    setSalvando(true);
    try {
      const r = await fetch(`/api/operacional/agenda/${form.id}`, { method: 'DELETE', headers: authHeaders() });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.message || 'Erro ao excluir.'); }
      setModalAberto(false);
      await carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  // Uma tag por visita no dia: "LOJA n/3" + selo verde no realizado.
  const renderDia = (dia) => (
    <>
      {(porDia[dia] || []).map((it, i) => {
        const v = SITUACAO_VISUAL[it.situacao] || SITUACAO_VISUAL.Previsto;
        if (it.TIPO === TIPO.TAREFA) {
          const icone = it.concluido ? <CheckCircle2 size={11} /> : <ClipboardList size={11} />;
          const texto = it.LOJA ? `${it.LOJA} · ${it.TITULO}` : it.TITULO;
          return (
            <TagDia
              key={`${it.ID}-${i}`}
              cor={v.cor}
              fundo={v.fundo}
              icone={icone}
              texto={texto}
              title={`Tarefa · ${it.CATEGORIA || '—'}: ${it.TITULO} — ${v.rotulo}${it.LOJA ? ` · ${it.LOJA}` : ''}`}
              onClick={() => abrirItem(it)}
            />
          );
        }
        const marca = it.situacao === 'Realizado' && !it.emAtraso ? <Check size={11} /> : null;
        return (
          <TagDia
            key={`${it.ID}-${i}`}
            cor={v.cor}
            fundo={v.fundo}
            icone={marca}
            texto={`${it.LOJA} ${it.totalFeitas}/3`}
            title={`${it.LOJA} — ${v.rotulo}${it.emAtraso ? ' (concluída em atraso)' : ''} · ${it.totalFeitas}/3 avaliações`}
            onClick={() => abrirItem(it)}
          />
        );
      })}
    </>
  );

  const btnNav = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', color: COR };

  return (
    <div style={{ fontFamily: FONTE }}>
      {/* Cabeçalho: navegação de mês + Agendar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button style={btnNav} onClick={() => irMes(-1)} aria-label="Mês anterior"><ChevronLeft size={18} /></button>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a', minWidth: 190, textAlign: 'center' }}>
            {MESES[mes - 1]} {ano}
          </h3>
          <button style={btnNav} onClick={() => irMes(1)} aria-label="Próximo mês"><ChevronRight size={18} /></button>
        </div>
        <button
          onClick={abrirNovo}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 8, border: 'none', background: COR, color: '#fff', fontWeight: 700, cursor: 'pointer' }}
        >
          <Plus size={16} /> Agendar
        </button>
      </div>

      {/* Placar por situação */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        {ORDEM_SITUACAO.map((s) => {
          const v = SITUACAO_VISUAL[s];
          return (
            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 8, background: v.fundo, color: v.cor, fontWeight: 700, fontSize: 13 }}>
              <span style={{ width: 10, height: 10, borderRadius: 999, background: v.cor }} />
              {v.rotulo}: {placar[s]}
            </div>
          );
        })}
      </div>

      {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 }}>{erro}</div>}

      {carregando
        ? <p style={{ color: '#64748b' }}>Carregando o calendário…</p>
        : <GridMes ano={ano} mes={mes} renderDia={renderDia} alturaCelula="118px" />}

      {/* Modal criar/editar */}
      {modalAberto && (
        <div style={modalOverlay} onClick={() => setModalAberto(false)}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader}>
              <strong style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0f172a' }}>
                {form.tipo === TIPO.TAREFA ? <ClipboardList size={18} color={COR} /> : <CalendarDays size={18} color={COR} />}
                {`${form.id ? 'Editar' : 'Novo'} ${form.tipo === TIPO.TAREFA ? 'tarefa' : 'visita'}`}
              </strong>
              <button style={btnX} onClick={() => setModalAberto(false)}><X size={18} /></button>
            </div>

            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Tipo do lançamento — só na criação; ao editar, o tipo é fixo. */}
              {!form.id && (
                <div style={{ display: 'flex', gap: 8 }}>
                  {[[TIPO.VISITA, 'Visita', CalendarDays], [TIPO.TAREFA, 'Tarefa', ClipboardList]].map(([val, label, Icone]) => {
                    const sel = form.tipo === val;
                    return (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setForm({ ...formVazio(), tipo: val })}
                        style={{
                          flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                          padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13,
                          border: `2px solid ${sel ? COR : '#e2e8f0'}`, background: sel ? COR : '#fff', color: sel ? '#fff' : '#475569',
                        }}
                      >
                        <Icone size={15} /> {label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Campos próprios da TAREFA */}
              {form.tipo === TIPO.TAREFA && (
                <>
                  <label style={rotuloStyle}>
                    Título
                    <input
                      type="text" style={inputStyle} maxLength={200} value={form.titulo}
                      placeholder="Ex.: Reunião de alinhamento mensal"
                      onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                    />
                  </label>
                  <label style={rotuloStyle}>
                    Categoria
                    <select style={inputStyle} value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}>
                      <option value="">— escolher —</option>
                      {CATEGORIAS_TAREFA.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>
                </>
              )}

              <label style={rotuloStyle}>
                {form.tipo === TIPO.TAREFA ? 'Loja (opcional)' : 'Loja'}
                <select style={inputStyle} value={form.loja} onChange={(e) => setForm({ ...form, loja: e.target.value })}>
                  <option value="">{form.tipo === TIPO.TAREFA ? '— sem loja —' : '— escolher —'}</option>
                  {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label style={rotuloStyle}>
                  Início
                  <input type="date" style={inputStyle} value={form.dataInicio} onChange={(e) => setForm({ ...form, dataInicio: e.target.value })} />
                </label>
                <label style={rotuloStyle}>
                  Fim
                  <input type="date" style={inputStyle} value={form.dataFim} onChange={(e) => setForm({ ...form, dataFim: e.target.value })} />
                </label>
              </div>

              <label style={rotuloStyle}>
                Observação (opcional)
                <textarea style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }} value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })} />
              </label>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginTop: 4 }}>
                {form.id ? (
                  confirmandoExcluir ? (
                    <button onClick={excluir} disabled={salvando} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, border: '1px solid #fecaca', background: '#fee2e2', color: '#b91c1c', fontWeight: 700, cursor: 'pointer' }}>
                      <Trash2 size={15} /> Confirmar exclusão
                    </button>
                  ) : (
                    <button onClick={() => setConfirmandoExcluir(true)} disabled={salvando} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#b91c1c', fontWeight: 700, cursor: 'pointer' }}>
                      <Trash2 size={15} /> Excluir
                    </button>
                  )
                ) : <span />}

                <button onClick={salvar} disabled={salvando} style={{ padding: '10px 18px', borderRadius: 8, border: 'none', background: COR, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                  {salvando ? 'Salvando…' : 'Salvar'}
                </button>
              </div>

              {/* VISITA (editar): o que já foi cumprido (derivado das avaliações). */}
              {form.id && form.tipo === TIPO.VISITA && (() => {
                const it = itens.find((x) => x.ID === form.id);
                if (!it) return null;
                return (
                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12, fontSize: 13, color: '#475569' }}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>Cumprimento (derivado das avaliações)</div>
                    {['a360', 'checklist', 'sa'].map((k) => (
                      <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <span style={{ color: it.feitas?.[k] ? '#047857' : '#cbd5e1' }}>{it.feitas?.[k] ? '✓' : '—'}</span>
                        {AVALIACAO_LABEL[k]}
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* TAREFA (editar): conclusão MANUAL (marcar/reabrir). */}
              {form.id && form.tipo === TIPO.TAREFA && (() => {
                const it = itens.find((x) => x.ID === form.id);
                const concluido = !!it?.concluido;
                return (
                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    {concluido ? (
                      <button onClick={() => concluirTarefa(false)} disabled={salvando} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontWeight: 700, cursor: 'pointer' }}>
                        <RotateCcw size={15} /> Reabrir tarefa
                      </button>
                    ) : (
                      <button onClick={() => concluirTarefa(true)} disabled={salvando} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, border: 'none', background: '#047857', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                        <CheckCircle2 size={15} /> Marcar como concluída
                      </button>
                    )}
                    {concluido && it?.CONCLUIDO_EM && (
                      <span style={{ fontSize: 12, color: '#64748b' }}>concluída em {it.CONCLUIDO_EM}</span>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarioOperacional;
